import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import { CalendarClock, AlertOctagon, Clock, ChevronRight, Loader2, Sparkles } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }); }
  catch { return iso; }
};

const PriorityChip = ({ p }) => {
  const map = {
    critical: "bg-red-100 text-red-700 border-red-300",
    alta:     "bg-orange-100 text-orange-700 border-orange-300",
    high:     "bg-orange-100 text-orange-700 border-orange-300",
  };
  return <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${map[p] || "bg-gray-100 text-gray-700 border-gray-300"}`}>{p}</span>;
};

const NoteRow = ({ n, onClick }) => {
  const days = n._days_until;
  const isOverdue = days < 0;
  const colorBar = isOverdue ? "bg-red-500" : days <= 3 ? "bg-orange-500" : days <= 7 ? "bg-amber-400" : "bg-[#007934]";
  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-stretch gap-3 hover:bg-gray-50 p-2.5 rounded-lg transition-colors group"
      data-testid={`pre-comite-note-${n.id}`}
    >
      <div className={`w-1 rounded-full ${colorBar} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <PriorityChip p={n.priority} />
          {n.project_ref && <span className="text-[10px] text-gray-500 truncate max-w-[180px]">{n.project_ref}</span>}
        </div>
        <p className="text-xs font-medium text-gray-900 mt-0.5 line-clamp-2">{n.title}</p>
        {n.external_blockers?.length > 0 && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {n.external_blockers.slice(0, 3).map((b, i) => (
              <span key={i} className="text-[9px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">⛔ {b}</span>
            ))}
          </div>
        )}
      </div>
      <div className="text-right flex flex-col items-end justify-center min-w-[58px]">
        <div className={`text-[10px] font-bold ${isOverdue ? "text-red-700" : "text-gray-700"}`}>
          {isOverdue ? `${Math.abs(days)}d atraso` : `+${days}d`}
        </div>
        <div className="text-[10px] text-gray-500">{fmtDate(n._milestone_parsed)}</div>
        <ChevronRight size={12} className="text-gray-300 group-hover:text-[#007934] mt-1" />
      </div>
    </button>
  );
};

export default function PreComiteWidget({ days = 14 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/committee/pre-comite-summary`, { params: { days } })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-2 text-gray-400 text-xs" data-testid="pre-comite-widget-loading">
        <Loader2 size={14} className="animate-spin"/> Cargando resumen pre-comité…
      </div>
    );
  }
  if (!data) return null;

  const overdue = data.overdue || [];
  const upcoming = data.upcoming || [];
  const total = overdue.length + upcoming.length;

  return (
    <div data-testid="pre-comite-widget" className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-orange-50/60 to-white">
        <div className="flex items-center gap-2">
          <CalendarClock size={15} className="text-orange-600" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Resumen Pre-Comité</p>
            <p className="text-sm font-semibold text-gray-900">Próximos {days} días · prioridad alta/crítica</p>
          </div>
        </div>
        <button
          onClick={() => navigate("/committee")}
          className="text-[11px] text-[#007934] font-semibold hover:underline flex items-center gap-1"
          data-testid="open-committee-btn"
        >
          Abrir comité <ChevronRight size={12} />
        </button>
      </div>

      {total === 0 && (
        <div className="p-6 text-center text-xs text-gray-400">
          <Sparkles size={20} className="mx-auto mb-2 text-gray-300"/>
          Sin compromisos urgentes en los próximos {days} días
        </div>
      )}

      {overdue.length > 0 && (
        <div className="border-b border-gray-100">
          <div className="px-4 py-2 bg-red-50/60 flex items-center gap-2">
            <AlertOctagon size={12} className="text-red-600"/>
            <span className="text-[10px] font-bold uppercase tracking-wider text-red-800">
              Atrasados ({overdue.length})
            </span>
          </div>
          <div className="px-2 py-1 space-y-1 max-h-56 overflow-y-auto">
            {overdue.map(n => (
              <NoteRow key={n.id} n={n} onClick={() => navigate("/committee")} />
            ))}
          </div>
        </div>
      )}

      {upcoming.length > 0 && (
        <div>
          <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
            <Clock size={12} className="text-gray-500"/>
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
              Próximos ({upcoming.length})
            </span>
          </div>
          <div className="px-2 py-1 space-y-1 max-h-56 overflow-y-auto">
            {upcoming.map(n => (
              <NoteRow key={n.id} n={n} onClick={() => navigate("/committee")} />
            ))}
          </div>
        </div>
      )}

      {data.counts?.no_milestone > 0 && (
        <div className="px-4 py-2 bg-gray-50/60 border-t border-gray-100 text-[10px] text-gray-500">
          + {data.counts.no_milestone} nota(s) prioritarias sin fecha de milestone definida
        </div>
      )}
    </div>
  );
}
